import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { cognitoAuthService } from '../../services/cognitoAuthService';

/**
 * Passwordless-only front door (2026-07-28 SSO migration, passwords retired):
 *  - "Sign in with Google" (federated; auto-links to existing accounts)
 *  - "Email Me a Sign-In Code" (Cognito EMAIL_OTP; works with any email;
 *    accounts auto-created on first sign-in)
 * There is no password UI anymore. Existing accounts keep their data — the
 * email address is the account key regardless of sign-in method.
 */
const AuthForm = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); // 'login', 'emailCode', 'emailCodeVerify'
  const [emailCodeFlow, setEmailCodeFlow] = useState(null); // 'signup' | 'signin'
  const [formData, setFormData] = useState({ email: '', confirmationCode: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGoogleSignIn = async () => {
    setErrorMessage('');
    setIsLoading(true);
    try {
      // Redirects the browser to Google; the app resumes on return
      await cognitoAuthService.signInWithGoogle();
    } catch (err) {
      if (/already a signed in user/i.test(err.message || '')) {
        // A session already exists — reload so the app picks it up
        window.location.reload();
        return;
      }
      setErrorMessage(err.message || 'Google sign-in failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleRequestEmailCode = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const result = await cognitoAuthService.requestEmailCode(formData.email);
      setEmailCodeFlow(result.flow);
      setFormData(prev => ({ ...prev, email: result.email, confirmationCode: '' }));
      setSuccessMessage(`We emailed a sign-in code to ${result.email}.`);
      setMode('emailCodeVerify');
    } catch (err) {
      setErrorMessage(err.message || 'Could not send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const user = await cognitoAuthService.verifyEmailCode(
        formData.confirmationCode,
        emailCodeFlow,
        formData.email
      );
      await onLogin(user);
    } catch (err) {
      setErrorMessage(err.message || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reusable "Sign in with Google" button
  const googleButton = (
    <button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={isLoading}
      className="w-full bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-700 font-medium py-3 rounded-lg border border-gray-300 transition-colors flex items-center justify-center shadow-sm"
    >
      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
      </svg>
      Sign in with Google
    </button>
  );

  const renderForm = () => {
    switch (mode) {
      case 'emailCode':
        return (
          <form onSubmit={handleRequestEmailCode} className="space-y-4">
            <div className="text-center mb-2">
              <p className="text-sm text-gray-600">
                Enter your email and we'll send you a one-time sign-in code — works for new and existing accounts.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="therapist@practice.com"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
              ) : (
                'Email Me a Code'
              )}
            </button>
          </form>
        );

      case 'emailCodeVerify':
        return (
          <form onSubmit={handleVerifyEmailCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sign-In Code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                name="confirmationCode"
                value={formData.confirmationCode}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
                placeholder="Enter code"
                required
                disabled={isLoading}
              />
              <p className="text-sm text-gray-600 mt-2">
                Enter the code we emailed to {formData.email}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Don't see it? Check your spam or junk folder — it can take a minute to arrive.
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 rounded-lg transition-colors"
            >
              {isLoading ? 'Verifying...' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={handleRequestEmailCode}
              disabled={isLoading}
              className="w-full text-blue-600 hover:text-blue-700 text-sm"
            >
              Didn't receive the code? Send a new one
            </button>
          </form>
        );

      default: // login — two buttons, no password
        return (
          <div className="space-y-4">
            {googleButton}

            <button
              type="button"
              onClick={() => { setMode('emailCode'); setErrorMessage(''); setSuccessMessage(''); }}
              disabled={isLoading}
              className="w-full bg-blue-50 hover:bg-blue-100 disabled:bg-gray-100 text-blue-700 font-medium py-3 rounded-lg border border-blue-200 transition-colors flex items-center justify-center"
            >
              <Mail className="w-5 h-5 mr-3" />
              Email Me a Sign-In Code
            </button>

            <p className="text-sm text-gray-600 text-center pt-2">
              Secure sign-in with Google or a one-time code sent to your email. New here? Your account is created automatically.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex flex-col items-center justify-center px-4 pb-20 md:pb-16 relative">
      {/* Top-right Logo - Hidden on mobile to prevent overlap */}
      <div className="absolute top-6 right-6 hidden md:block">
        <img src="/chartara_labs_logo.png" alt="Chartara Labs" className="h-20 w-auto opacity-80"/>
      </div>

      {/* Main Heading - Responsive sizing */}
      <div className="text-center mb-8 md:mb-12 mt-4 md:mt-0">
        <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-2 md:mb-4">Chartara Notes</h1>
        <p className="text-xl md:text-3xl text-blue-100 font-semibold">AI Scribe and Clinical Decision Support System</p>
      </div>

      {/* Auth Card */}
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          {mode === 'login' && 'Therapist Sign In'}
          {mode === 'emailCode' && 'Sign In with Email Code'}
          {mode === 'emailCodeVerify' && 'Enter Your Code'}
        </h2>

        {errorMessage && (
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
            <p className="text-red-800 text-sm">{errorMessage}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 mb-4">
            <p className="text-blue-800 text-sm">{successMessage}</p>
          </div>
        )}

        {renderForm()}

        {/* Mode Switching */}
        <div className="mt-6 text-center space-y-2">
          {(mode === 'emailCode' || mode === 'emailCodeVerify') && (
            <button
              onClick={() => {
                setMode('login');
                setErrorMessage('');
                setSuccessMessage('');
              }}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Footer Features - Responsive grid and spacing */}
      <div className="fixed bottom-0 left-0 right-0 bg-black bg-opacity-20 py-2 md:py-4">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4 px-4 md:px-8 text-center">
          <div className="text-white text-xs md:text-sm font-medium">User Friendly</div>
          <div className="text-white text-xs md:text-sm font-medium">Customizable</div>
          <div className="text-white text-xs md:text-sm font-medium">Voice Dictation</div>
          <div className="text-white text-xs md:text-sm font-medium">HIPAA Secure</div>
          <div className="text-white text-xs md:text-sm font-medium">Fast</div>
          <div className="text-white text-xs md:text-sm font-medium">Accurate</div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
